<template>
  <div class="BaseSelectSearch">
    <!-- Predefined Search  -->
    <v-select
      v-if="options.length > 1"
      v-model="inputType"
      @input="query.value = ''"
      aria-label="Universal search selector with input"
      class="BaseSelectSearch__selector"
      data-cy="base-select-search-select-input"
      :clearable="false"
      :options="options"
    />
    <base-client-search
      v-if="inputType.toLowerCase() === 'clients'"
      @selected="goToClientActivity"
      class="BaseSelectSearch__client-input"
      :reset-after-selected="true"
    />
    <base-debtor-search
      v-if="inputType.toLowerCase() === 'debtors'"
      @selected="goToDebtorProfile"
      class="BaseSelectSearch__debtor-input"
      :hide-debtor-not-found="true"
      :reset-after-selected="true"
    />
    <base-input
      v-if="['load #', 'invoice #'].includes(inputType.toLowerCase())"
      v-model.trim="query.value"
      @keydown.enter.stop="searchQuery"
      class="BaseSelectSearch__input"
      data-cy="base-select-search-query-input"
      :instructions="instructions.QUERY_INSTRUCTIONS"
      :placeholder="`Search by ${inputType.toLowerCase() === 'load #' ? 'load #' : 'invoice #'}`"
      :valid="query.valid"
    />
    <base-button
      @click="searchQuery"
      class="BaseSelectSearch__search-btn bg-blue fc-white"
      data-cy="base-select-search-search-btn"
      :disabled="!['load #', 'invoice #'].includes(inputType.toLowerCase()) || !query.value || disableSearchBtn"
    >
      Search
    </base-button>
  </div>
</template>

<script>
// Packages
import isEqual from 'lodash.isequal'
// Helpers
import DOMPurify from 'dompurify'
// Components
import BaseButton from './base-button.vue'
import BaseClientSearch from './base-client-search.vue'
import BaseDebtorSearch from './base-debtor-search.vue'
import BaseInput from './base-input.vue'
// Mixins
import {
  ValidationMixin,
} from '../utils/validation-mixin'

export default {
  name: 'BaseSelectSearch',

  components: {
    BaseButton,
    BaseClientSearch,
    BaseDebtorSearch,
    BaseInput,
  },

  mixins: [ ValidationMixin ],

  created () {
    if (this.$auth.user) {
      // Init routes
      this.setSelectorOptions()
    }
    else {
      this.visible = false
    }
    this.setQueryFromQueryString()
  },

  data () {
    return {
      disableSearchBtn: false,
      inputType: 'clients',
      query: {
        valid: true,
        value: '',
      },
      options: [],
      visible: true,
    }
  },

  watch: {
    $route (newRoute, oldRoute) {
      if (newRoute !== oldRoute) {
        this.setQueryFromQueryString()
      }
    },

    '$auth.user' () {
      this.setSelectorOptions()
    },
  },

  methods: {
    goToClientActivity (client) {
      // TODO: why would param ever be null???
      // Protection from an odd `null` value from the client/debtor select component
      // eslint-disable-next-line max-len
      // https://sentry.io/organizations/bobtail/issues/1863511331/events/3e88d115b7834d3681fa16d2cb527e22/?environment=production&project=5223295&query=is%3Aunresolved&statsPeriod=7d
      if (!client) {
        return
      }
      // Check for navigation duplication
      if (this.$route.name.includes('client') && this.$route.params.id === client.id.toString()) {
        return
      }
      this.$router.push({
        name: 'client',
        params: {
          id: client.id.toString(),
        },
      })
    },

    goToDebtorProfile (debtor) {
      // TODO: why would param ever be null???
      // Protection from an odd `null` value from the client/debtor select component
      // eslint-disable-next-line max-len
      // https://sentry.io/organizations/bobtail/issues/1863511331/events/3e88d115b7834d3681fa16d2cb527e22/?environment=production&project=5223295&query=is%3Aunresolved&statsPeriod=7d
      if (!debtor) {
        return
      }
      // Check for navigation duplication
      if (this.$route.name.includes('debtor') && this.$route.params.id === debtor.id.toString()) {
        return
      }
      this.$router.push({
        name: 'debtor',
        params: {
          id: debtor.id.toString(),
        },
      })
    },

    resetData () {
      this.$set(this.query, 'valid', true)
      this.$set(this.query, 'value', '')
    },

    async searchQuery () {
      this.disableSearchBtn = true

      if (!this.validation()) {
        this.disableSearchBtn = false
        return
      }

      // Remove search info from SS when searching via universal search, because
      // it will screw up going to invoice details from a pending invoice since
      // it could contain a pending invoice not in the invoice IDs  in SS,
      // which will only result in `id-not-in-ids` or `ids-empty`
      sessionStorage.removeItem('search')
      this.$store.commit('CLEAR_INVOICES')
      this.progressStart()

      try {
        const queryParams = {}

        if (this.inputType.toLowerCase() === 'load #') {
          queryParams.loadNumber = DOMPurify.sanitize(this.query.value)
        }

        if (this.inputType.toLowerCase() === 'invoice #') {
          queryParams.ids = DOMPurify.sanitize(this.query.value)
        }
        this.progressFinish()
        this.resetData()

        if (!isEqual(this.$route.query, { ...queryParams })) {
          this.$router.push({
            query: { ...queryParams },
            name: 'search-results',
          })
        }
      }
      catch (error) {
        this.captureSentryEvent(
          'Employee Selector "Query Search" Error',
          {
            config: error.config,
            data: this.$data,
            details: error,
            props: this.$props,
            response: error.response,
          }
        )
        this.CError(error)
        this.requestFailure({
          message: 'There was an issue searching for potential matching invoices',
          title: 'Request error',
        })
      }

      this.disableSearchBtn = false
    },

    setQueryFromQueryString () {
      // Do NOT autofill the input when a query (load or ID) variable exists, because we want
      // the select input to erase after each search and route change
      // https://app.zenhub.com/workspaces/bobtail-5c64d9b0b66dba3bb32bda6e/issues/fs-bobtail/bobtail/1660
      // https://bobtail.atlassian.net/browse/SM-1048

      this.resetData()

      if (this.$route.query.loadNumber) {
        this.inputType = 'load #'
      }
      if (this.$route.query.ids) {
        this.inputType = 'invoice #'
      }
    },

    setSelectorOptions () {
      if (this.$auth.user.client) {
        this.options = [ 'load #' ]
      }
      else {
        this.options = [ 'clients', 'debtors', 'load #', 'invoice #' ]
      }
      // Default the selector to the first option (regardless of user)
      this.inputType = this.options[0]
    },

    validation () {
      let valid = true

      if (this.query.value) {
        if (
          (this.inputType === 'load #' && !this.isValidQuery(this.query.value))
          || (this.inputType === 'invoice #' && !this.isValidID(this.query.value))
        ) {
          this.$set(this.query, 'valid', false)
          valid = false
        }
      }
      else {
        this.$set(this.query, 'valid', true)
      }

      return valid
    },
  },
}
</script>

<style lang="sass">
.BaseSelectSearch
  +flex(row, $align: center)

  &__client-input,
  &__debtor-input
    .BaseUserSearch__input
      border-radius: 0

  &__client-input.BaseUserSearch,
  &__debtor-input.BaseUserSearch,
  &__input .BaseInput__input
    width: rem(350px)

  &__input
    .BaseInput__input
      border-radius: 0

  &__search-btn
    border-radius: 0 $border-radius $border-radius 0

  &__selector
    // flex-shrink keeps the fucking selector from changing widths when open
    // which is dumb
    flex-shrink: 0
    width: rem(140px)

    .vs__dropdown-toggle
      background-color: $borders
      border-radius: $border-radius 0 0 $border-radius
      border-right: 0

    .vs__dropdown-menu
      min-width: 0
      width: rem(141px)

    .vs__open-indicator
      color: $text-dark

    .vs__selected
      color: $text-dark

</style>
